/**
 * Single source of truth for the company's legal and contact identity.
 *
 * These values appear on the Company page, in the footer on every route,
 * throughout the legal pages and in the Organization structured data.
 * Keeping one copy means a registration change is a one-line edit and cannot
 * leave the JSON-LD disagreeing with the visible page.
 */

export const COMPANY = {
  legalName: 'Skabro s. r. o.',
  tradingName: 'Skabro',
  legalForm: 'Spoločnosť s ručením obmedzeným (s. r. o.)',

  /** Company registration number. */
  ico: '56788207',
  /** Tax identification number. */
  dic: '2122432895',
  /** VAT identification number. */
  icDph: 'SK2122432895',
  /*
   * Names the act. "VAT payer under §4" renders in a legal box on /company as a
   * bare section number with no statute attached to it, which is the one place
   * a reader is entitled to assume every reference resolves. The FAQ already
   * spelled it out; the fact grid did not.
   */
  vatStatus: 'VAT payer under §4 of the Slovak VAT Act',

  incorporated: '13 February 2025',
  /** ISO 8601, for structured data. */
  incorporatedIso: '2025-02-13',

  register: {
    court: 'Mestský súd Bratislava III',
    section: 'oddiel Sro, vložka č. 185366/B',
    /**
     * The same entry in English, for the running prose in Terms § 2 and Privacy
     * § 1.
     *
     * Added because both pages had "section Sro, insert no. 185366/B" typed out
     * as a literal - the one registration fact on the site that existed in three
     * places instead of one, which is exactly what this file exists to prevent.
     * A register change would have updated the Slovak form and the footer and
     * quietly left the two legal pages stating the old insert number.
     */
    sectionEn: 'section Sro, insert no. 185366/B',
    short: 'Sro 185366/B, MS Bratislava III',
  },

  address: {
    street: 'Zuzany Chalupovej 4029/9',
    /** As printed on the site: postal code and city on one line. */
    locality: '851 07 Bratislava - Petržalka',
    localityFormal: '851 07 Bratislava - mestská časť Petržalka',
    city: 'Bratislava',
    postalCode: '851 07',
    country: 'Slovakia',
    countryCode: 'SK',
  },

  statutory: {
    name: 'Yaroslav Abrasimov',
    role: 'konateľ',
    note: 'Acting independently for the company',
    /**
     * Personal profiles for the founder's `Person` node in the JSON-LD graph.
     *
     * Structured data only - deliberately not rendered anywhere on the page.
     * They exist so search engines and answer engines can resolve "Skabro" to a
     * real, corroborated person, which is the entity signal the site otherwise
     * has no way to give. The company reads as a company; the graph knows who
     * is behind it.
     */
    profiles: [
      'https://www.linkedin.com/in/yaroslav-abrasimov/',
      'https://github.com/Ferri0',
    ] as readonly string[],
  },

  /**
   * The supervisory authority, required on the website itself by § 4(1) of
   * Slovak Act No. 22/2004 Coll. on electronic commerce.
   *
   * That section obliges an information-society service provider to make the
   * name and address of the body supervising its activity permanently and
   * directly accessible, alongside the identification data § 3a of the
   * Commercial Code requires. Every other item on the § 4 list was already on
   * /company and in Terms § 2. This one was absent - the kind of omission that
   * stays invisible until somebody reads the statute next to the page.
   *
   * The Slovak Trade Inspection is the designated body for that Act. Publishing
   * it concedes nothing about who we contract with: the duty attaches to
   * operating the site, not to whether the visitor is a consumer.
   */
  supervision: {
    authority: 'Slovenská obchodná inšpekcia',
    authorityEn: 'Slovak Trade Inspection',
    office: 'Inšpektorát SOI pre Bratislavský kraj',
    address: 'Bajkalská 21/A, P. O. BOX č. 5, 820 07 Bratislava',
    email: 'ba@soi.sk',
    web: 'https://www.soi.sk',
  },

  /**
   * The data protection authority, for the Art. 77 complaint route.
   *
   * Here rather than inline in the Privacy Policy so the address cannot drift
   * out of agreement with the copy on /company, for the same reason every other
   * registration fact lives in this file.
   */
  dataProtectionAuthority: {
    name: 'Úrad na ochranu osobných údajov Slovenskej republiky',
    address: 'Hraničná 12, 820 07 Bratislava',
  },

  email: 'hello@skabro.eu',

  /**
   * THE TELEPHONE NUMBER IS DELIBERATELY NOT IN THIS FILE.
   *
   * It used to be, and from here it reached six places: /contact, /company,
   * Terms § 2, the Accessibility statement, and - worst of all - the JSON-LD
   * `telephone` property, which is machine-readable by design. Anything that can
   * read a page could take it, for free, at scale.
   *
   * It now lives in the shared forms backend (`forms.sites.contact_phone`) and is
   * handed out by the `reveal-contact` Edge Function after a server-side
   * reCAPTCHA check. /contact asks for it on a click; nowhere else publishes it.
   * See src/lib/phone.ts and src/components/PhoneReveal.tsx.
   *
   * Consequences worth knowing before adding it back:
   *   - A literal here ships in the JS bundle whether or not anything renders it,
   *     so a constant "for convenience" undoes the whole mechanism silently.
   *   - The number is not secret. It is on invoices and in the commercial
   *     register, and any person who asks should get it. What this removes is
   *     bulk harvesting, not access.
   *
   * The mask below is the only phone-shaped thing the site ships. It exists so
   * the reveal button has something to sit next to: a control offering "show the
   * number" reads as far more trustworthy when the shape of the answer is already
   * visible. `+421 915` is a country code and an operator prefix shared by
   * millions of Slovak numbers, so it identifies nobody.
   */
  phoneMask: '+421 915 ••• •••',

  openingHours: 'Monday - Friday, 9:00 - 17:00 CET',
  /*
   * Hedged on purpose. This renders unhedged on Home, so an absolute phrasing
   * would read as a commitment rather than a norm.
   */
  replyTime: 'Usually within one working day',
} as const;

/**
 * Public profiles belonging to the *company*, for `Organization.sameAs`.
 *
 * Empty until they exist. This is the single highest-value structured-data
 * field the site is missing: `sameAs` is how search and answer engines
 * corroborate that an organisation named on a page is the same organisation
 * described elsewhere. A LinkedIn company page and a GitHub organisation are
 * the two worth having; the ORSR register listing is a third, and it has the
 * useful property of being a government source that confirms every
 * registration fact on /company.
 *
 * Add the URLs here and they flow into the graph on the next build.
 */
export const COMPANY_PROFILES: readonly string[] = [];

export const SITE = {
  name: 'Skabro',
  domain: 'skabro.eu',
  url: 'https://skabro.eu',
  locale: 'en',
  localeFull: 'en_GB',
  tagline: 'eCommerce engineering and custom software for businesses across Europe.',
  /**
   * One self-contained sentence stating who the company is and what it does.
   *
   * Written to be quoted whole. Answer engines cite short factual sentences
   * that carry their own context; the tagline above is marketing copy and
   * carries none. This is rendered on /company and reused in llms.txt.
   */
  factualSummary:
    'Skabro s. r. o. is a Slovak software company, registered in Bratislava in February 2025, ' +
    'building eCommerce systems, platform applications and business integrations on Adobe ' +
    'Commerce, Shopify, Shopware and BigCommerce for business clients across the European Union.',
  /** Shown on every legal page. */
  legalLastUpdated: '26 July 2026',
  legalLastUpdatedIso: '2026-07-26',
} as const;

/**
 * Formats the footer copyright notice for a given year.
 *
 * A range from incorporation rather than a bare current year: a notice reading
 * "© 2026" on a company that has published since 2025 understates the period
 * actually claimed, and the range is the conventional form for exactly that
 * reason. It collapses to a single year while the two are the same, so it reads
 * correctly in the company's first year too.
 *
 * The year is passed in rather than read here - see the Footer for why it
 * cannot be `new Date().getFullYear()` at this level.
 */
export function copyrightNotice(year: number): string {
  const from = Number(COMPANY.incorporatedIso.slice(0, 4));
  const to = Math.max(from, year);
  return to > from ? `${from}-${to}` : `${from}`;
}
